/* V2 progressive enhancement. No cart requests, variant mutation or dependencies. */
(() => {
  if (window.peitwayDTC) return;
  window.peitwayDTC = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionAllowed = () => !reduced.matches && document.querySelector('[data-pw-v2-motion]')?.dataset.pwV2Motion !== 'false';
  const motionListen = (callback) => {
    if (reduced.addEventListener) reduced.addEventListener('change', callback);
    else reduced.addListener(callback);
    return () => {
      if (reduced.removeEventListener) reduced.removeEventListener('change', callback);
      else reduced.removeListener(callback);
    };
  };

  class PeitwayMarquee extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const button = this.querySelector('[data-pw-marquee-pause]');
      if (!button) return;
      this.paused = false;
      this.inView = true;
      const fit = () => {
        const viewport = this.querySelector('.pw-marquee-window');
        this.style.setProperty('--pw-marquee-width', `${viewport?.clientWidth || 0}px`);
      };
      const update = () => {
        const enabled = motionAllowed();
        this.classList.toggle('is-animated', enabled);
        this.classList.toggle('is-playing', enabled && !this.paused && this.inView && !document.hidden);
        button.setAttribute('aria-pressed', String(this.paused));
        button.setAttribute('aria-label', this.paused ? 'Reanudar movimiento' : 'Pausar movimiento');
        const symbol = button.querySelector('span');
        if (symbol) symbol.textContent = this.paused ? '▷' : 'Ⅱ';
      };
      const toggle = () => { this.paused = !this.paused; update(); };
      button.addEventListener('click', toggle);
      document.addEventListener('visibilitychange', update);
      document.addEventListener('peitway:motion', update);
      const removeMotion = motionListen(update);
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        update();
      }) : null;
      observer?.observe(this);
      const resize = 'ResizeObserver' in window ? new ResizeObserver(fit) : null;
      resize?.observe(this);
      window.addEventListener('resize', fit, { passive: true });
      fit();
      update();
      this.cleanup = () => {
        button.removeEventListener('click', toggle);
        document.removeEventListener('visibilitychange', update);
        document.removeEventListener('peitway:motion', update);
        removeMotion();
        observer?.disconnect();
        resize?.disconnect();
        window.removeEventListener('resize', fit);
      };
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  }
  if (!customElements.get('pw-marquee')) customElements.define('pw-marquee', PeitwayMarquee);

  class PeitwaySocial extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const track = this.querySelector('.pw-social-track');
      if (!track) return;
      const previous = this.querySelector('[data-pw-social-prev]');
      const next = this.querySelector('[data-pw-social-next]');
      const update = () => {
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      const move = (direction) => {
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
        const step = (card?.getBoundingClientRect().width || track.clientWidth) + gap;
        track.scrollBy({ left: direction * step, behavior: motionAllowed() ? 'smooth' : 'auto' });
      };
      const back = () => move(-1);
      const forward = () => move(1);
      const pauseOthers = (event) => {
        if (!(event.target instanceof HTMLVideoElement)) return;
        this.querySelectorAll('video').forEach(video => { if (video !== event.target && !video.paused) video.pause(); });
      };
      previous?.addEventListener('click', back);
      next?.addEventListener('click', forward);
      track.addEventListener('scroll', update, { passive: true });
      this.addEventListener('play', pauseOthers, true);
      const resize = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
      resize?.observe(track);
      window.addEventListener('resize', update, { passive: true });
      this.classList.add('is-ready');
      update();
      this.cleanup = () => {
        previous?.removeEventListener('click', back);
        next?.removeEventListener('click', forward);
        track.removeEventListener('scroll', update);
        this.removeEventListener('play', pauseOthers, true);
        window.removeEventListener('resize', update);
        resize?.disconnect();
      };
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  }
  if (!customElements.get('pw-social')) customElements.define('pw-social', PeitwaySocial);

  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.replace('pw-reveal-pending', 'pw-reveal-done');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -18px 0px' }) : null;
  const reveal = (scope) => {
    scope.querySelectorAll('[data-pw-reveal]').forEach(element => {
      if (element.classList.contains('pw-reveal-done')) return;
      if (!motionAllowed() || !revealObserver || element.getBoundingClientRect().top < window.innerHeight) {
        element.classList.remove('pw-reveal-pending');
        revealObserver?.unobserve(element);
        return;
      }
      element.classList.add('pw-reveal-pending');
      revealObserver.observe(element);
    });
  };
  /* Read-only mirror of the native price; the native product form remains the only purchase state. */
  const priceObservers = new Map();
  const connectPrice = (scope) => {
    scope.querySelectorAll('[data-pw-product]').forEach(product => {
      if (priceObservers.has(product)) return;
      const price = product.querySelector('[data-pw-main-price]');
      const mirror = product.querySelector('[data-pw-current-option-price]');
      if (!price || !mirror) return;
      const sync = () => {
        if (mirror.innerHTML !== price.innerHTML) mirror.innerHTML = price.innerHTML;
        mirror.hidden = price.hidden || price.classList.contains('hidden');
      };
      const observer = new MutationObserver(sync);
      observer.observe(price, { childList: true, subtree: true, attributes: true });
      priceObservers.set(product, observer);
      sync();
    });
  };
  const decorateInlineWhatsApp = (scope = document) => {
    const root = scope?.querySelectorAll ? scope : document;
    root.querySelectorAll('.pw-product .product__info-container > .shopify-app-block').forEach(block => {
      if (block.classList.contains('pw-seedgrow-inline')) return;
      const signature = block.querySelector(
        'a[href*="wa.me"], a[href*="whatsapp.com"], a[href*="api.whatsapp.com"], [class*="seedgrow" i], [class*="sgwa" i]'
      );
      const text = (block.textContent || '').toLowerCase();
      if (signature || text.includes('whatsapp') || text.includes('chat with us')) block.classList.add('pw-seedgrow-inline');
    });
  };
  let whatsappFrame = 0;
  const whatsappObserver = new MutationObserver(() => {
    if (whatsappFrame) return;
    whatsappFrame = requestAnimationFrame(() => {
      whatsappFrame = 0;
      decorateInlineWhatsApp(document);
    });
  });

  const init = (scope = document) => {
    for (const [product, observer] of priceObservers) {
      if (!product.isConnected) { observer.disconnect(); priceObservers.delete(product); }
    }
    reveal(document);
    connectPrice(scope);
    decorateInlineWhatsApp(scope);
    document.dispatchEvent(new CustomEvent('peitway:motion'));
  };
  motionListen(() => reveal(document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); whatsappObserver.observe(document.documentElement, { childList: true, subtree: true }); }, { once: true });
  else { init(); whatsappObserver.observe(document.documentElement, { childList: true, subtree: true }); }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-pw-reveal]').forEach(element => revealObserver?.unobserve(element));
    for (const [product, observer] of priceObservers) {
      if (event.target.contains(product)) { observer.disconnect(); priceObservers.delete(product); }
    }
  });
  document.addEventListener('shopify:block:select', event => {
    event.target.classList.remove('pw-reveal-pending');
    revealObserver?.unobserve(event.target);
    const card = event.target.closest('.pw-social-card');
    if (card) card.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
  });
})();
